import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import { setReflowGroupsAction } from 'editor/src/store/design/slice';
import { ReflowGroup, ReflowRow } from 'editor/src/store/design/types';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import updateHiddenElementsOperation from './updateHiddenElementsOperation';

let counter = 0;
const nextId = (prefix: string): string => {
  counter += 1;
  return `${prefix}-${counter}-${Math.floor(Math.random() * 100000).toString(36)}`;
};

export const cloneGroups = (groups: ReflowGroup[]): ReflowGroup[] =>
  groups.map((g) => ({
    id: g.id,
    name: g.name,
    rows: g.rows.map((r) => ({ id: r.id, optional: r.optional, elementUuids: [...r.elementUuids], shiftY: r.shiftY })),
  }));

const getGroups = (state: RootState, spreadIndex: number): ReflowGroup[] =>
  state.design.designData?.spreads[spreadIndex]?.reflowGroups ?? [];

const commit = (dispatch: ThunkDispatch, spreadIndex: number, next: ReflowGroup[]): void => {
  batch(() => {
    dispatch(setReflowGroupsAction({ spreadIndex, reflowGroups: next }));
    // Any structural mutation of reflow groups can change which rows are
    // collapsed → which elements should be hidden. Mirror the Conditions
    // pattern (e.g. removeConditionOptionOperation) and recompute in the
    // same batch so the render sees a consistent state.
    dispatch(updateHiddenElementsOperation());
  });
};

export const addReflowGroupOperation =
  (spreadIndex: number, defaultName: string) => (dispatch: ThunkDispatch, getState: () => RootState) => {
    const current = getGroups(getState(), spreadIndex);
    const groups = cloneGroups(current);
    groups.push({ id: nextId('rg'), name: `${defaultName} ${groups.length + 1}`, rows: [] });
    commit(dispatch, spreadIndex, groups);
  };

export const renameReflowGroupOperation =
  (spreadIndex: number, groupId: string, name: string) => (dispatch: ThunkDispatch, getState: () => RootState) => {
    const groups = cloneGroups(getGroups(getState(), spreadIndex));
    const target = groups.find((g) => g.id === groupId);
    if (!target) {
      return;
    }
    target.name = name;
    commit(dispatch, spreadIndex, groups);
  };

export const deleteReflowGroupOperation =
  (spreadIndex: number, groupId: string) => (dispatch: ThunkDispatch, getState: () => RootState) => {
    const groups = cloneGroups(getGroups(getState(), spreadIndex)).filter((g) => g.id !== groupId);
    commit(dispatch, spreadIndex, groups);
  };

export const addReflowRowOperation =
  (spreadIndex: number, groupId: string, elementUuids: number[] = [], optional = true) =>
  (dispatch: ThunkDispatch, getState: () => RootState) => {
    const groups = cloneGroups(getGroups(getState(), spreadIndex));
    const target = groups.find((g) => g.id === groupId);
    if (!target) {
      return;
    }
    const row: ReflowRow = { id: nextId('rr'), optional, elementUuids: [...elementUuids] };
    target.rows.push(row);
    commit(dispatch, spreadIndex, groups);
  };

export const removeReflowRowOperation =
  (spreadIndex: number, groupId: string, rowId: string) => (dispatch: ThunkDispatch, getState: () => RootState) => {
    const groups = cloneGroups(getGroups(getState(), spreadIndex));
    const target = groups.find((g) => g.id === groupId);
    if (!target) {
      return;
    }
    target.rows = target.rows.filter((r) => r.id !== rowId);
    commit(dispatch, spreadIndex, groups);
  };

export const setReflowRowOptionalOperation =
  (spreadIndex: number, groupId: string, rowId: string, optional: boolean) =>
  (dispatch: ThunkDispatch, getState: () => RootState) => {
    const groups = cloneGroups(getGroups(getState(), spreadIndex));
    const target = groups.find((g) => g.id === groupId);
    const row = target?.rows.find((r) => r.id === rowId);
    if (!target || !row) {
      return;
    }
    row.optional = optional;
    commit(dispatch, spreadIndex, groups);
  };

export const addElementToReflowRowOperation =
  (spreadIndex: number, groupId: string, rowId: string, elementUuid: number) =>
  (dispatch: ThunkDispatch, getState: () => RootState) => {
    const groups = cloneGroups(getGroups(getState(), spreadIndex));
    const target = groups.find((g) => g.id === groupId);
    const row = target?.rows.find((r) => r.id === rowId);
    if (!target || !row) {
      return;
    }
    if (row.elementUuids.includes(elementUuid)) {
      return;
    }
    // Also strip the uuid from any other row in any group so an element belongs
    // to exactly one row at a time — same invariant as Conditions.
    groups.forEach((g) =>
      g.rows.forEach((r) => {
        r.elementUuids = r.elementUuids.filter((u) => u !== elementUuid);
      }),
    );
    row.elementUuids.push(elementUuid);
    commit(dispatch, spreadIndex, groups);
  };

export const removeElementFromReflowRowOperation =
  (spreadIndex: number, groupId: string, rowId: string, elementUuid: number) =>
  (dispatch: ThunkDispatch, getState: () => RootState) => {
    const groups = cloneGroups(getGroups(getState(), spreadIndex));
    const target = groups.find((g) => g.id === groupId);
    const row = target?.rows.find((r) => r.id === rowId);
    if (!target || !row) {
      return;
    }
    row.elementUuids = row.elementUuids.filter((u) => u !== elementUuid);
    commit(dispatch, spreadIndex, groups);
  };
